"use client";
/**
 * The app's one reader of Convex data (CLAUDE.md → Reads). Every page reads in the browser through these hooks, and
 * `dw/reads-through-cached-hooks` refuses a query hook imported from anywhere else.
 *
 * - They read through the app's query cache (ConvexQueryCacheProvider in lib/convex/client): a read stays subscribed
 *   a few seconds after it unmounts, so a page that replaces its own loading state finds the result the loading state
 *   already holds, and a hover's prewarm (lib/navigation/page-reads) is the read the page then asks for.
 * - The authed ones wait for the Convex auth handshake. A raw query fired on mount races it: it runs unauthenticated,
 *   the server refuses it or answers empty, then it re-runs once auth lands, a flash of wrong state
 *   (LC-MSG-AUTH-RACE-01).
 * - Every list asks with one pagination id, so a prewarmed first page is the page the list asks for.
 */

import { usePaginatedQuery, useQueries, useQuery } from "convex-helpers/react/cache/hooks";
import { type PaginatedQueryArgs, type PaginatedQueryReference, useConvexAuth } from "convex/react";

import type { FunctionArgs, FunctionReference, FunctionReturnType, paginationOptsValidator } from "convex/server";
import type { Infer } from "convex/values";
import { useState } from "react";

/** The pagination id every useAuthedPaginatedQuery list asks with. convex-helpers' cached hook gives each list the
 *  same id (0) so a list's pages can be cached, which also lets a prewarm (lib/navigation/page-reads) ask for exactly
 *  the pages a list will. Convex's own hook draws a fresh id per list, which no prewarm can match. */
export const LIST_PAGINATION_ID = 0;

/** A list's first page, as useAuthedPaginatedQuery asks for it. */
export function firstPageOptions(numItems: number): Infer<typeof paginationOptsValidator> {
    return { numItems, cursor: null, id: LIST_PAGINATION_ID };
}

/** A paginated list, once the member is signed in to Convex. */
export function useAuthedPaginatedQuery<Q extends PaginatedQueryReference>(query: Q, args: PaginatedQueryArgs<Q> | "skip", options: { initialNumItems: number; }) {
    const { isAuthenticated } = useConvexAuth();
    return usePaginatedQuery(query, isAuthenticated ? args : "skip", options);
}

/** A query, once the member is signed in to Convex. */
export function useAuthedQuery<Q extends FunctionReference<"query">>(query: Q, args: FunctionArgs<Q> | "skip") {
    const { isAuthenticated } = useConvexAuth();
    return useQuery(query, isAuthenticated ? args : "skip");
}

/** A query whose refusal the page answers itself: the error comes back as a value instead of throwing to the error
 *  page (the contact page shows a contact the member may not read as not found). */
export function useAuthedQueryResult<Q extends FunctionReference<"query">>(query: Q, args: FunctionArgs<Q> | "skip"): FunctionReturnType<Q> | Error | undefined {
    const { isAuthenticated } = useConvexAuth();
    const { result } = useQueries(isAuthenticated && args !== "skip" ? { result: { query, args } } : {});
    return result;
}

/** A query a signed-out visitor's page makes (a portal's published agreement): no wait for auth, which never comes. */
export function usePublicQuery<Q extends FunctionReference<"query">>(query: Q, args: FunctionArgs<Q> | "skip") {
    return useQuery(query, args);
}

/**
 * What a mounted view shows while its arguments change: the last answer that landed, until the new one does, so a
 * chart's period or the contact list's filters swap the view rather than emptying it. `key` names what an answer
 * belongs to beyond its arguments (the contact list's tab); an answer under another key is not shown. A first load
 * has no last answer, so it still draws the loading state (CLAUDE.md → Skeletons). Taken while rendering: React's
 * pattern for state that follows a value.
 */
export function useLastAnswer<T>(answer: T | undefined, key = ""): T | undefined {
    const [shown, setShown] = useState<ReadonlyMap<string, T>>(() => answer === undefined ? new Map() : new Map([[key, answer]]));
    if (answer !== undefined && shown.get(key) !== answer) {
        setShown(new Map(shown).set(key, answer));
    }
    return answer ?? shown.get(key);
}
